import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { Spinner } from 'react-bootstrap';
import { authService } from '../../../utils/authService';
import {
  fetchCoordinatorSessions,
  fetchSessionSummary,
  SessionSummaryResponse,
} from './sessionSummaryApi';
import './SessionSummary.css';
import './Dashboard.css';

type PieSlice = { name: string; value: number; color: string };

interface FiltersData {
  courses: string[];
  batches: Record<string, string[]>;
  subjects: Record<string, Record<string, string[]>>;
}

const TIER_COLORS = {
  high: '#66bb6a',
  medium: '#ffb300',
  low: '#ef5350',
} as const;

const selectStyle: React.CSSProperties = {
  height: 36,
  fontSize: 14,
  fontFamily: 'inherit',
};

const menuItemStyle: React.CSSProperties = {
  fontSize: 14,
  fontFamily: 'inherit',
};

const labelStyle: React.CSSProperties = {
  fontSize: 14,
  fontFamily: 'inherit',
};

function resolveFilterSelection(
  data: FiltersData,
  preferred?: { course?: string; batch?: string; subject?: string | null },
): { course: string; batch: string; subject: string } {
  const courses = data.courses || [];
  const course =
    preferred?.course && courses.includes(preferred.course)
      ? preferred.course
      : courses[0] || '';

  const batches = course ? data.batches[course] || [] : [];
  const batch =
    preferred?.batch && batches.includes(preferred.batch)
      ? preferred.batch
      : batches[0] || '';

  const subjects =
    course && batch ? data.subjects[course]?.[batch] || [] : [];
  const subject =
    preferred?.subject && subjects.includes(preferred.subject)
      ? preferred.subject
      : subjects[0] || '';

  return { course, batch, subject };
}

function readSavedDashboardFilters(): {
  course?: string;
  batch?: string;
  subject?: string | null;
} | null {
  const saved = sessionStorage.getItem('dashboardFilters');
  if (!saved) return null;
  try {
    return JSON.parse(saved) as {
      course?: string;
      batch?: string;
      subject?: string | null;
    };
  } catch {
    return null;
  }
}

function pctLabel(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${Math.round(value)}%`;
}

function numLabel(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return String(value);
}

function pctOf(part: number, whole: number): number | null {
  if (!whole || whole <= 0 || part < 0) return null;
  return Math.round((part / whole) * 1000) / 10;
}

function buildTierItems(
  high: number | null,
  medium: number | null,
  low: number | null,
): PieSlice[] {
  return [
    { name: 'High', value: high ?? 0, color: TIER_COLORS.high },
    { name: 'Medium', value: medium ?? 0, color: TIER_COLORS.medium },
    { name: 'Low', value: low ?? 0, color: TIER_COLORS.low },
  ];
}

function DonutChart({
  data,
  centerMain,
  centerSub,
  emptyLabel,
}: {
  data: PieSlice[];
  centerMain?: string;
  centerSub?: string;
  emptyLabel: string;
}) {
  const slices = data.filter((d) => d.value > 0);
  if (!slices.length) {
    return (
      <div className="donut-chart-empty">{emptyLabel}</div>
    );
  }
  return (
    <div className="donut-chart-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="58%"
            outerRadius="88%"
            startAngle={90}
            endAngle={slices.length === 1 ? -269.99 : -270}
            paddingAngle={0}
            stroke="none"
            strokeWidth={0}
          >
            {slices.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {centerMain ? (
        <div className="donut-chart-center">
          <span className="donut-chart-center-main">{centerMain}</span>
          {centerSub ? (
            <span className="donut-chart-center-sub">{centerSub}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ChartLegend({ items }: { items: PieSlice[] }) {
  return (
    <div className="tier-legend">
      {items.map((item) => (
        <div key={item.name} className="tier-legend-item">
          <span className="tier-legend-bar" style={{ background: item.color }} />
          <span className="tier-legend-name">{item.name}</span>
          <span className="tier-legend-value">{Math.round(item.value)}%</span>
        </div>
      ))}
    </div>
  );
}

function TierChart({
  title,
  items,
  emptyLabel,
}: {
  title: string;
  items: PieSlice[];
  emptyLabel: string;
}) {
  const hasData = items.some((i) => i.value > 0);
  return (
    <div className="tier-chart">
      <p className="tier-chart-title">{title}</p>
      <div className="tier-chart-body">
        <div className="chart-donut-col">
          <DonutChart data={items} emptyLabel={emptyLabel} />
        </div>
        {hasData ? <ChartLegend items={items} /> : null}
      </div>
    </div>
  );
}


const SessionSummary: React.FC = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState('');
  const [filtersData, setFiltersData] = useState<FiltersData | null>(null);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [course, setCourse] = useState('');
  const [batch, setBatch] = useState('');
  const [subject, setSubject] = useState('');
  const [sessionOptions, setSessionOptions] = useState<{ id: string; title: string }[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [summary, setSummary] = useState<SessionSummaryResponse | null>(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filtersInitializedRef = useRef(false);

  const batchOptions = useMemo(
    () => (course && filtersData ? filtersData.batches[course] || [] : []),
    [filtersData, course],
  );

  const subjectOptions = useMemo(
    () =>
      course && batch && filtersData
        ? filtersData.subjects[course]?.[batch] || []
        : [],
    [filtersData, course, batch],
  );

  const safeBatch = batchOptions.includes(batch) ? batch : '';
  const safeSubject = subjectOptions.includes(subject) ? subject : '';

  const persistFilters = (next: { course: string; batch: string; subject: string }) => {
    sessionStorage.setItem(
      'dashboardFilters',
      JSON.stringify({
        course: next.course,
        batch: next.batch,
        subject: next.subject || null,
        project: null,
      }),
    );
  };

  useEffect(() => {
    if (!sessionStorage.getItem('Coordinator')) navigate('/');
  }, [navigate]);

  useEffect(() => {
    const id = sessionStorage.getItem('Id');
    if (id) setUserId(id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoadingFilters(true);
      try {
        const response = await authService.get(`coordinator/filters/${userId}/`);
        if (cancelled) return;
        const data = response.data as FiltersData;
        setFiltersData(data);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Failed to load course/batch filters.');
      } finally {
        if (!cancelled) setLoadingFilters(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!filtersData || filtersInitializedRef.current) return;
    filtersInitializedRef.current = true;

    const saved = readSavedDashboardFilters();
    const resolved = resolveFilterSelection(filtersData, saved ?? undefined);
    setCourse(resolved.course);
    setBatch(resolved.batch);
    setSubject(resolved.subject);
  }, [filtersData]);

  useEffect(() => {
    if (!filtersData || !course) return;
    const nextBatch = batchOptions.includes(batch) ? batch : batchOptions[0] || '';
    const nextSubject =
      nextBatch && filtersData.subjects[course]?.[nextBatch]?.includes(subject)
        ? subject
        : nextBatch
          ? filtersData.subjects[course]?.[nextBatch]?.[0] || ''
          : '';
    if (nextBatch === batch && nextSubject === subject) return;
    setBatch(nextBatch);
    setSubject(nextSubject);
    setSelectedSessionId('');
    setSummary(null);
    setSessionOptions([]);
  }, [filtersData, course, batch, subject, batchOptions]);

  const handleCourseChange = (value: string) => {
    if (!filtersData) return;
    const resolved = resolveFilterSelection(filtersData, { course: value });
    setCourse(resolved.course);
    setBatch(resolved.batch);
    setSubject(resolved.subject);
    setSelectedSessionId('');
    setSummary(null);
    setSessionOptions([]);
    setError(null);
    if (resolved.course && resolved.batch) {
      persistFilters(resolved);
    }
  };

  const handleBatchChange = (value: string) => {
    if (!filtersData || !course) return;
    const resolved = resolveFilterSelection(filtersData, { course, batch: value });
    setBatch(resolved.batch);
    setSubject(resolved.subject);
    setSelectedSessionId('');
    setSummary(null);
    setSessionOptions([]);
    setError(null);
    if (resolved.batch) {
      persistFilters({ course, batch: resolved.batch, subject: resolved.subject });
    }
  };

  const handleSubjectChange = (value: string) => {
    setSubject(value);
    setSelectedSessionId('');
    setSummary(null);
    setSessionOptions([]);
    setError(null);
    if (course && batch) {
      persistFilters({ course, batch, subject: value });
    }
  };

  const handleApply = useCallback(async () => {
    if (!userId || !course || !safeBatch || !safeSubject) {
      setError('Select course, batch, and subject before applying.');
      return;
    }
    setLoadingSessions(true);
    setError(null);
    setSelectedSessionId('');
    setSummary(null);
    try {
      const data = await fetchCoordinatorSessions({
        course_name: course,
        batch_name: safeBatch,
        user_id: userId,
        subject_name: safeSubject,
      });
      const options =
        (data.Session_options?.length
          ? data.Session_options.map((row) => ({
              title: row.session_title,
              id: String(row.session_id),
            }))
          : Object.entries(data.Session_title_dict || {}).map(([title, id]) => ({
              title,
              id: String(id),
            }))) || [];
      setSessionOptions(options);
      if (options.length === 1) {
        setSelectedSessionId(options[0].id);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load sessions for the selected course, batch, and subject.');
      setSessionOptions([]);
    } finally {
      setLoadingSessions(false);
    }
  }, [userId, course, safeBatch, safeSubject]);

  useEffect(() => {
    if (loadingFilters || !userId || !course || !safeBatch || !safeSubject) return;
    void handleApply();
  }, [loadingFilters, userId, course, safeBatch, safeSubject, handleApply]);

  useEffect(() => {
    if (!selectedSessionId) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingSummary(true);
      setError(null);
      try {
        const data = await fetchSessionSummary(selectedSessionId);
        if (!cancelled) setSummary(data);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setSummary(null);
          setError('Failed to load session summary.');
        }
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSessionId]);

  const isPageLoading = loadingFilters || loadingSessions || loadingSummary;

  const feedbackItems = useMemo(() => {
    if (!summary) return [];
    const f = summary.feedback;
    return buildTierItems(f.high_percent, f.medium_percent, f.low_percent);
  }, [summary]);

  const quizItems = useMemo(() => {
    if (!summary) return [];
    const q = summary.quiz_scores;
    return buildTierItems(q.high_percent, q.medium_percent, q.low_percent);
  }, [summary]);

  const derived = useMemo(() => {
    if (!summary) return null;
    const b = summary.basic;
    const e = summary.engagement;
    const attended = b.attended || 0;
    const durationMin = b.duration_minutes || 0;
    const qaMin = e.qa_duration_minutes || 0;
    return {
      questionsPct: pctOf(e.questions_raised, attended),
      qaSessionPct: pctOf(qaMin, durationMin),
      doubtsResolvedPct:
        e.doubts_raised > 0
          ? pctOf(e.doubts_resolved, e.doubts_raised)
          : null,
      distractedPct: pctOf(summary.distraction.students_distracted, attended),
    };
  }, [summary]);

  const basic = summary?.basic;

  return (
    <div className="container-fluid p-0 px-2" style={{ marginTop: '12px' }}>
      <Card>
        <CardContent
          style={{
            height: 'calc(100vh - 25px)',
            overflow: 'hidden',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            mb={1}
            style={{ flexShrink: 0 }}
          >
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}
            >
              <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                <InputLabel style={labelStyle}>Course</InputLabel>
                <Select
                  value={course}
                  label="Course"
                  disabled={loadingFilters || !filtersData?.courses.length}
                  onChange={(e: SelectChangeEvent) => handleCourseChange(e.target.value)}
                  style={selectStyle}
                >
                  <MenuItem value="" style={menuItemStyle}>
                    Select Course
                  </MenuItem>
                  {(filtersData?.courses || []).map((c) => (
                    <MenuItem key={c} value={c} style={menuItemStyle}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                <InputLabel style={labelStyle}>Batch</InputLabel>
                <Select
                  value={safeBatch}
                  label="Batch"
                  disabled={!course || loadingFilters || batchOptions.length === 0}
                  onChange={(e: SelectChangeEvent) => handleBatchChange(e.target.value)}
                  style={selectStyle}
                >
                  <MenuItem value="" style={menuItemStyle}>
                    Select Batch
                  </MenuItem>
                  {batchOptions.map((b) => (
                    <MenuItem key={b} value={b} style={menuItemStyle}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                <InputLabel style={labelStyle}>Subject</InputLabel>
                <Select
                  value={safeSubject}
                  label="Subject"
                  disabled={
                    !course || !safeBatch || loadingFilters || subjectOptions.length === 0
                  }
                  onChange={(e: SelectChangeEvent) => handleSubjectChange(e.target.value)}
                  style={selectStyle}
                >
                  <MenuItem value="" style={menuItemStyle}>
                    Select Subject
                  </MenuItem>
                  {subjectOptions.map((s) => (
                    <MenuItem key={s} value={s} style={menuItemStyle}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                <InputLabel style={labelStyle}>Session</InputLabel>
                <Select
                  value={selectedSessionId}
                  label="Session"
                  disabled={
                    !course ||
                    !safeBatch ||
                    !safeSubject ||
                    loadingSessions ||
                    (!loadingSessions && sessionOptions.length === 0)
                  }
                  onChange={(e: SelectChangeEvent) => setSelectedSessionId(e.target.value)}
                  style={selectStyle}
                >
                  <MenuItem value="" style={menuItemStyle}>
                    {loadingSessions
                      ? 'Loading sessions…'
                      : sessionOptions.length === 0
                        ? 'No sessions found'
                        : 'Select Session'}
                  </MenuItem>
                  {sessionOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id} style={menuItemStyle}>
                      {opt.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {error ? (
            <Typography sx={{ color: '#c62828', mb: 1, flexShrink: 0 }}>{error}</Typography>
          ) : null}

          <Paper
            elevation={0}
            className="session-summary-content"
            style={{
              marginTop: '8px',
              flexGrow: 1,
              width: '100%',
              minWidth: 0,
              overflowX: 'hidden',
              overflowY: 'auto',
              position: 'relative',
              border: '1px solid #e0e0e0',
              boxSizing: 'border-box',
            }}
          >
            {isPageLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '240px',
                }}
              >
                <Spinner animation="border" />
              </Box>
            ) : null}

            {!isPageLoading && summary && basic && derived ? (
              <>
                  <div className="session-summary-section-body">
                    <div className="basic-data-grid">
                      {[
                        ['Live Session Id', basic.live_session_id],
                        ['Date', basic.date ?? '—'],
                        ['Start', basic.start ?? '—'],
                        ['End', basic.end ?? '—'],
                        ['Duration', basic.duration_label],
                        ['Invitees', numLabel(basic.invitees)],
                        ['Attended', numLabel(basic.attended)],
                        ['Attendance %', pctLabel(basic.attendance_percent)],
                        ['Subject', basic.subject ?? '—'],
                        ['Topics', basic.topics.length ? basic.topics.join(', ') : '—'],
                      ].map(([label, value]) => (
                        <div key={label} className="basic-data-item">
                          <label>{label}</label>
                          <span>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                <div className="charts-row">
                  <TierChart
                    title="Feedback Data"
                    items={feedbackItems}
                    emptyLabel="No feedback ratings yet"
                  />
                  <TierChart
                    title="Quiz Scores"
                    items={quizItems}
                    emptyLabel="No quiz submissions yet"
                  />
                </div>

                <div className="row gx-3 gy-3 split-row">
                  <div className="col-12 col-lg-6">
                    <div className="split-panel">
                      <p className="section-kicker">Engagement Data</p>
                      <div className="session-summary-section-body">
                      <div className="metrics-row">
                        {[
                          {
                            label: 'Questions Raised',
                            value: numLabel(summary.engagement.questions_raised),
                            sub:
                              derived.questionsPct != null
                                ? `${derived.questionsPct}% of attended`
                                : '—'
                          },
                          {
                            label: 'Q&A Duration',
                            value: summary.engagement.qa_duration_label || '—',
                            sub:
                              derived.qaSessionPct != null
                                ? `${derived.qaSessionPct}% of session`
                                : '—',
                          },
                          {
                            label: 'Doubts Raised',
                            value: numLabel(summary.engagement.doubts_raised),
                          },
                          {
                            label: 'Doubts Resolved',
                            value: numLabel(summary.engagement.doubts_resolved),
                            sub:
                              derived.doubtsResolvedPct != null
                                ? `${derived.doubtsResolvedPct}% resolved`
                                : '—',
                          },
                        ].map((item) => (
                          <div key={item.label} className="metric-tile p-2">
                            <label>{item.label}</label>
                            <span>{item.value}</span>
                            <p className="metric-tile-sub">{item.sub}</p>
                          </div>
                        ))}
                      </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="split-panel">
                      <p className="section-kicker">Distraction Data</p>
                      <div className="session-summary-section-body">
                      <div className="metrics-row metrics-row-3">
                        <div className="metric-tile p-2">
                          <label>Students Distracted</label>
                          <span>{numLabel(summary.distraction.students_distracted)}</span>
                          <p className="metric-tile-sub">
                            {derived.distractedPct != null
                              ? `${derived.distractedPct}% of attendees`
                              : '—'}
                          </p>
                        </div>
                        <div className="metric-tile p-2 pb-0">
                          <label>Distracted Duration (Students)</label>
                          <div className="gauge-wrap">
                            <DonutChart
                              data={
                                summary.distraction.distracted_duration_students_percent != null
                                  ? [
                                      {
                                        name: 'Distracted',
                                        value:
                                          summary.distraction.distracted_duration_students_percent,
                                        color: '#ff9800',
                                      },
                                      {
                                        name: 'Other',
                                        value: Math.max(
                                          0,
                                          100 -
                                            (summary.distraction
                                              .distracted_duration_students_percent || 0),
                                        ),
                                        color: '#eceff1',
                                      },
                                    ]
                                  : []
                              }
                              centerMain={pctLabel(
                                summary.distraction.distracted_duration_students_percent,
                              )}
                              emptyLabel="No distraction data"
                            />
                          </div>
                        </div>
                        <div className="metric-tile p-2 pb-0">
                          <label>Distracted Duration (Class)</label>
                          <div className="gauge-wrap">
                            <DonutChart
                              data={
                                summary.distraction.distracted_duration_class_percent != null
                                  ? [
                                      {
                                        name: 'Distracted',
                                        value:
                                          summary.distraction.distracted_duration_class_percent,
                                        color: '#ff9800',
                                      },
                                      {
                                        name: 'Other',
                                        value: Math.max(
                                          0,
                                          100 -
                                            (summary.distraction.distracted_duration_class_percent ||
                                              0),
                                        ),
                                        color: '#eceff1',
                                      },
                                    ]
                                  : []
                              }
                              centerMain={pctLabel(
                                summary.distraction.distracted_duration_class_percent,
                              )}
                              emptyLabel="No distraction data"
                            />
                          </div>
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {!isPageLoading && !summary ? (
              <div className="session-summary-empty">
                {!safeSubject
                  ? 'Select course, batch, and subject to load sessions.'
                  : sessionOptions.length === 0
                    ? 'No live sessions found for the selected filters.'
                    : 'Select a session to view the live session report.'}
              </div>
            ) : null}
          </Paper>
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionSummary;
